import { Component, OnInit, HostListener, EventEmitter, Output, ViewChild, ElementRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthService, SessionUser, UserDcr } from '../../services/auth.service';
import { FilterService } from '../../services/filter.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavigationBlockService, NavigationBlockOptions } from '../../services/navigation-block.service';
import { CreationStateService } from '../../services/creation-state.service';
import { WORLD_COUNTRIES } from '../../utils/countries';
import { NotificationCenterComponent } from '../notification-center/notification-center.component';
import { DocNavigationService, TocItem } from '../../services/doc-navigation.service';
import { ImpersonateApiService, ImpersonateUserListItem } from '../../services/impersonate-api.service';
import { UserPermissions, userHasPermission } from '../../services/permissions';
import { ExperimentalFeaturesPanelComponent } from '../experimental-features/experimental-features-panel.component';
import { ExperimentalFeaturesService } from '../../services/experimental-features.service';
import { NotifyBroadcastPanelComponent } from '../notify-broadcast/notify-broadcast-panel.component';
import { NotificationBackendService } from '../../services/notification-backend.service';

type ProductOption = 'home' | 'dashboard' | 'vision' | 'reach' | 'tracking-scripts' | 'cmp';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, FormsModule, TranslateModule, NotificationCenterComponent, ExperimentalFeaturesPanelComponent, NotifyBroadcastPanelComponent],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  standalone: true
})
export class SidebarComponent implements OnInit {
  @ViewChild(NotificationCenterComponent) notificationCenter?: NotificationCenterComponent;
  @ViewChild('dcrOptionsList') dcrOptionsList?: ElementRef<HTMLUListElement>;
  @ViewChild('entitySelectorBtn') entitySelectorBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('impersonateBtnRef') impersonateBtnRef?: ElementRef<HTMLButtonElement>;

  expandedFilters: { [key: string]: boolean } = {
    dateRange: false,
    device: false,
    trafficSource: false,
    audience: false,
    goal: false,
    country: false
  };

  currentUser: SessionUser | null = null;
  userMenuOpen = false;
  dcrDropdownOpen = false;
  currentLanguage: string = 'it';
  isMouseInBottomSection = false;
  isMouseInUserMenu = false;
  dcrSearchTerm = '';
  keyboardFocusedIndex = -1;
  entitySelectorActive = false;
  selectedProduct: ProductOption = 'home';
  applyFiltersToAllCharts = true;
  selectedCountry = 'Italy';
  countrySearchTerm = '';
  readonly availableCountries = WORLD_COUNTRIES;
  bottomSectionExpanded = false;
  reportingMenuOpen = false;
  // Whether the accordion content of the currently selected product is expanded.
  // Independent from `selectedProduct` so the user can collapse it without leaving the product.
  accordionExpanded = true;
  // URL as of the last completed navigation, used to detect when a tree submenu's
  // section is *entered* (to auto-open it) vs. navigated-within (where the user's
  // manual open/close choice must be respected).
  private lastNavigationUrl = '';
  impersonateUsers: ImpersonateUserListItem[] = [];
  selectedImpersonateUserId = '';
  impersonateLoading = false;
  impersonateDropdownOpen = false;
  impersonateDropdownStyle: { bottom: string; left: string; width: string } = { bottom: '0px', left: '10px', width: '190px' };
  experimentalFeaturesPanelOpen = false;
  notifyBroadcastPanelOpen = false;
  @Output() changePasswordRequested = new EventEmitter<void>();
  private isInCreationMode = false;
  docMenuItems$: Observable<TocItem[]>;
  readonly entitySyncPending$ = inject(AuthService).entitySyncPending$;
  readonly unreadCount$ = inject(NotificationBackendService).unreadCount$;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    public filterService: FilterService,
    private authService: AuthService,
    private translateService: TranslateService,
    private navigationBlockService: NavigationBlockService,
    private creationStateService: CreationStateService,
    private docNavService: DocNavigationService,
    private impersonateApiService: ImpersonateApiService,
    private experimentalFeatures: ExperimentalFeaturesService
  ) {
    this.docMenuItems$ = this.docNavService.menu$;
  }

  ngOnInit(): void {
    // Always enable "apply filters to all charts"
    this.filterService.setApplyFiltersToAllCharts(true);

    // Initialize current language from localStorage or default
    const savedLang = localStorage.getItem('language');
    this.currentLanguage = savedLang || this.translateService.currentLang || this.translateService.defaultLang || 'it';
    if (savedLang && savedLang !== this.translateService.currentLang) {
      this.translateService.use(savedLang);
    }

    // Subscribe to current user changes
    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        const prevId = this.currentUser?.ID;
        this.currentUser = user;
        this.selectedImpersonateUserId = this.authService.isImpersonating ? (user?.ID ?? '') : '';

        if (prevId !== undefined && user?.ID !== prevId) {
          this.redirectIfCurrentProductUnavailable();
        }
      });

    // Subscribe to creation mode changes to disable entity selector
    this.creationStateService.isInCreationMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isCreating => {
        this.isInCreationMode = isCreating;
      });

    this.filterService.applyFiltersToAllCharts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.applyFiltersToAllCharts = value;
      });

    // Initialize selected product based on current route
    this.updateSelectedProductFromRoute();

    // Auto-open the tree submenu matching the URL on first load (e.g. deep link),
    // same as entering that section via in-app navigation would.
    this.syncMenuOpenOnNavigation('', this.router.url);
    this.lastNavigationUrl = this.router.url;

    // Subscribe to router events to update selected product
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.syncMenuOpenOnNavigation(this.lastNavigationUrl, event.urlAfterRedirects);
          this.lastNavigationUrl = event.urlAfterRedirects;
        }
        this.updateSelectedProductFromRoute();
      });
  }

  private syncMenuOpenOnNavigation(previousUrl: string, currentUrl: string): void {
    const sections: Array<{ isActive: (url: string) => boolean; open: () => void }> = [
      {
        isActive: url => url.startsWith('/reach/reports/'),
        open: () => { this.reportingMenuOpen = true; }
      }
    ];

    for (const section of sections) {
      if (section.isActive(currentUrl) && !section.isActive(previousUrl)) {
        section.open();
      }
    }
  }

  private redirectIfCurrentProductUnavailable(): void {
    const unavailable =
      (this.selectedProduct === 'dashboard' && !this.canAccessAnalyticsDashboard) ||
      (this.selectedProduct === 'reach' && !this.isReachAvailable) ||
      (this.selectedProduct === 'vision' && (!this.authService.hasPermission(UserPermissions.VISION) || !this.authService.currentDcrHasVisionAccess())) ||
      (this.selectedProduct === 'tracking-scripts' && !this.canAccessTrackingScripts) ||
      (this.selectedProduct === 'cmp' && !this.canAccessCmp);

    if (unavailable) {
      this.selectedProduct = 'home';
      this.router.navigate(['/home']);
    }
  }

  private updateSelectedProductFromRoute(): void {
    const path = this.router.url;
    if (!this.currentUser || path.startsWith('/login') || path.startsWith('/password-recovery')) {
      return;
    }

    let product: ProductOption;
    if (path.startsWith('/dashboard')) {
      product = 'dashboard';
    } else if (path.startsWith('/vision')) {
      product = 'vision';
    } else if (path.startsWith('/reach')) {
      product = 'reach';
    } else if (path.startsWith('/tracking-scripts')) {
      product = 'tracking-scripts';
    } else if (path.startsWith('/cmp')) {
      product = 'cmp';
    } else {
      product = 'home';
    }

    // Entering a different product's section always re-expands its accordion, but
    // navigating within the already-selected product must respect a manual collapse.
    if (product !== this.selectedProduct) {
      this.accordionExpanded = true;
    }
    this.selectedProduct = product;
  }

  private getCurrentDcrOptions(): UserDcr[] {
    const all = this.currentUser?.availableDCRs ?? [];
    const sorted = [...all].sort((a, b) => (a.Name ?? a.id ?? '').localeCompare(b.Name ?? b.id ?? ''));
    if (this.selectedProduct !== 'vision') {
      return sorted;
    }
    // On Vision: Vision-enabled first, then others
    const withVision = sorted.filter(d => d.hasVisionAccess);
    const withoutVision = sorted.filter(d => !d.hasVisionAccess);
    return [...withVision, ...withoutVision];
  }

  get isGlobalScopeSelected(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  get showGlobalScopeEntry(): boolean {
    if (!this.hasGlobalDataAccess) return false;
    const term = this.dcrSearchTerm.trim().toLowerCase();
    // Filtra sull'etichetta effettivamente mostrata: era hardcoded 'admin', quindi dopo il rename
    // della label cercare "global" faceva sparire la voce e cercare "admin" la mostrava a vuoto.
    const label = this.translateService.instant('sidebar.dcr.globalScope').toLowerCase();
    return !term || label.includes(term);
  }

  async selectGlobalScope(event?: Event): Promise<void> {
    event?.stopPropagation();
    const allowed = await this.runWithNavigationBlock(async () => {
      this.authService.setGlobalScope();
    });
    if (!allowed) return;
    this.dcrDropdownOpen = false;
    this.userMenuOpen = false;
    this.dcrSearchTerm = '';
    this.keyboardFocusedIndex = -1;
    this.entitySyncPending$.pipe(filter(p => !p), take(1)).subscribe(() => {
      this.entitySelectorActive = true;
      // Selecting an entity without access to the current product (e.g. switching
      // to "Admin" while on Vision) must leave that product, otherwise its views
      // error out with no valid DCR context.
      this.redirectIfCurrentProductUnavailable();
    });
  }

  canSelectDcr(dcr: UserDcr): boolean {
    if (this.selectedProduct !== 'vision') return true;
    return !!dcr.hasVisionAccess;
  }

  isFirstNonVisionDcr(dcr: UserDcr, index: number): boolean {
    if (this.selectedProduct !== 'vision' || !!dcr.hasVisionAccess) return false;
    const list = this.filteredDcrs;
    return index === 0 || !!list[index - 1]?.hasVisionAccess;
  }

  toggleFilter(filterName: string) {
    this.expandedFilters[filterName] = !this.expandedFilters[filterName];
  }

  async navigate(path: string): Promise<void> {
    await this.runWithNavigationBlock(async () => {
      await this.router.navigate([path]);
    }, { expectRouteChange: true });
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  get isReportingActive(): boolean {
    return this.router.url.startsWith('/reach/reports/');
  }

  toggleReportingMenu(): void {
    this.reportingMenuOpen = !this.reportingMenuOpen;
  }

  get availableAudiences() {
    return this.filterService.getAvailableAudiences();
  }

  get selectedAudiences() {
    return this.filterService.getSelectedAudiences();
  }

  toggleAudience(audienceId: string) {
    this.filterService.toggleAudience(audienceId);
  }

  isAudienceDisabled(audienceId: string): boolean {
    return this.filterService.isAudienceDisabled(audienceId);
  }

  get selectedDateRange(): '7days' | '14days' | '30days' | '90days' | 'year' {
    return this.filterService.getSelectedDateRange();
  }

  get dateRangeLabel(): string {
    return `sidebar.filters.dateRanges.${this.selectedDateRange}`;
  }

  selectDateRange(range: '7days' | '14days' | '30days' | '90days' | 'year') {
    this.filterService.setDateRange(range);
  }

  // Device filter methods
  get availableDevices() {
    return this.filterService.getAvailableDevices();
  }

  get selectedDevices() {
    return this.filterService.getSelectedDevices();
  }

  toggleDevice(deviceId: string) {
    this.filterService.toggleDevice(deviceId);
  }

  // Traffic Source filter methods
  get availableTrafficSources() {
    return this.filterService.getAvailableTrafficSources();
  }

  get selectedTrafficSources() {
    return this.filterService.getSelectedTrafficSources();
  }

  toggleTrafficSource(sourceId: string) {
    this.filterService.toggleTrafficSource(sourceId);
  }

  onApplyFiltersToggle(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.filterService.setApplyFiltersToAllCharts(!!target?.checked);
  }

  // Goal filter methods
  get availableGoals() {
    return this.filterService.getAvailableGoals();
  }

  get selectedGoal() {
    return this.filterService.getSelectedGoal();
  }

  selectGoal(goalId: string) {
    this.filterService.selectGoal(goalId);
  }

  get hasActiveFilters(): boolean {
    return (
      this.filterService.getSelectedDevices().length > 0 ||
      this.filterService.getSelectedTrafficSources().length > 0 ||
      !!this.filterService.getSelectedGoal()
    );
  }

  resetFilters(event: MouseEvent): void {
    event.stopPropagation();
    this.filterService.clearNonAudienceFilters();
  }

  get showFilters(): boolean {
    const path = this.router.url;
    const isSpecial =
      path.startsWith('/vision/comparison') ||
      path.startsWith('/reach/custom-imports') ||
      path.startsWith('/reach/destinations');
    // The filters panel is only rendered inside the Vision product, so it should be
    // gated by Vision access (vision.all), not by the admin dashboard permission.
    return !isSpecial && !this.isAudienceRoute && this.canAccessVision;
  }

  get isAudienceRoute(): boolean {
    const path = this.router.url;
    return path.startsWith('/reach/audiences') ||
           path.startsWith('/reach/goals') ||
           path.startsWith('/reach/custom-imports') ||
           path.startsWith('/reach/destinations');
  }

  get canAccessDashboard(): boolean {
    return this.authService.hasPermission(UserPermissions.ADMIN_DASHBOARD);
  }

  get canAccessAnalyticsDashboard(): boolean {
    return this.authService.hasPermission('analytics.all');
  }

  get canAccessAudience(): boolean {
    return this.authService.hasPermission(UserPermissions.REACH_AUDIENCE);
  }

  get canAccessAudienceTools(): boolean {
    return this.authService.hasPermission(UserPermissions.REACH_AUDIENCE);
  }

  get canAccessDestinations(): boolean {
    return this.authService.hasPermission(UserPermissions.ADMIN_DASHBOARD);
  }

  get isOnHomePage(): boolean {
    const url = this.router.url;
    return url === '/home' || url === '/';
  }

  // Experimental feature: render the products menu as an accordion (each product
  // expands to reveal its sub-menu). Falls back to the classic two-level layout
  // when the feature isn't enabled.
  get accordionMenuEnabled(): boolean {
    return this.experimentalFeatures.isEnabled('accordionProductsMenu');
  }

  get isVisionAvailable(): boolean {
    return !!this.currentUser && this.authService.currentDcrHasVisionAccess();
  }

  // Show the Vision product only when the user could actually use it: they hold the feature
  // permission ('all' or 'vision.all') AND at least one of their entities is Vision-enabled.
  // Users failing either condition don't see the button at all; those passing both keep it
  // visible but disabled while the currently-selected entity lacks Vision access.
  get canAccessVision(): boolean {
    return this.authService.hasPermission(UserPermissions.VISION)
      && (this.currentUser?.availableDCRs ?? []).some(d => !!d.hasVisionAccess);
  }

  get isReachAvailable(): boolean {
    if (!this.currentUser) {
      return false;
    }
    return this.authService.hasPermission(UserPermissions.REACH) ||
      this.authService.hasPermission(UserPermissions.REACH_AUDIENCE) ||
      this.authService.hasPermission(UserPermissions.REACH_CUSTOM_IMPORT) ||
      this.authService.hasPermission(UserPermissions.REACH_GOAL) ||
      this.authService.hasPermission(UserPermissions.REACH_REPORTING);
  }

  get canAccessTrackingScripts(): boolean {
    return userHasPermission(this.authService.currentUserValue?.permissions, UserPermissions.SCRIPTS);
  }

  get isTrackingScriptsRoute(): boolean {
    return this.router.url.startsWith('/tracking-scripts');
  }

  get canAccessCmp(): boolean {
    return userHasPermission(this.authService.currentUserValue?.permissions, UserPermissions.CMP);
  }

  get canAccessCmpAdmin(): boolean {
    const permissions = this.authService.currentUserValue?.permissions;
    return userHasPermission(permissions, UserPermissions.CMP) && userHasPermission(permissions, UserPermissions.ADMIN_DASHBOARD);
  }

  get isCmpRoute(): boolean {
    return this.router.url.startsWith('/cmp');
  }

  get canAccessReports(): boolean {
    return this.authService.hasPermission(UserPermissions.REACH_REPORTING) && !!this.authService.getActiveDcrId();
  }

  get hasGlobalDataAccess(): boolean {
    return this.authService.hasGlobalDataAccess();
  }

  get hasGlobalDataScope(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  get canImpersonate(): boolean {
    // L'accesso globale lo decide sempre il service, che legge già lo snapshot quando si sta
    // impersonando: qui serve solo scegliere da dove prendere i *permessi*, che durante
    // un'impersonation devono essere quelli dell'utente reale, non della vittima.
    if (!this.authService.realUserHasGlobalDataAccess) {
      return false;
    }
    const saved = localStorage.getItem('admin_original_session');
    if (saved) {
      try {
        const { userData } = JSON.parse(saved);
        return userHasPermission(userData?.permissions, UserPermissions.SPECIAL_FEATURES_CAN_IMPERSONATE);
      } catch {}
    }
    return userHasPermission(this.authService.currentUserValue?.permissions, UserPermissions.SPECIAL_FEATURES_CAN_IMPERSONATE);
  }

  get canAccessAdminPanel(): boolean {
    return userHasPermission(this.authService.currentUserValue?.permissions, UserPermissions.ADMIN_DASHBOARD);
  }



  get isActiveDcrSandbox(): boolean {
    const id = this.authService.getActiveDcrId();
    if (!id) return false;
    const dcr = this.currentUser?.availableDCRs?.find(d => d.id === id);
    return !!dcr?.isSandbox;
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
    if (this.userMenuOpen) {
      this.dcrDropdownOpen = false;
    }
    if (!this.userMenuOpen) {
      this.dcrSearchTerm = '';
    }
  }

  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  onUserSummaryKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleUserMenu(event);
    }
  }

  onDropdownClick(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close user menu if clicked outside
    const userDropdown = target.closest('.user-dropdown-bottom');
    const userBtn = target.closest('.user-btn');
    if (!userDropdown && !userBtn) {
      this.userMenuOpen = false;
    }

    // Close DCR dropdown if clicked outside
    this.dcrDropdownOpen = false;
    this.dcrSearchTerm = '';
    this.entitySelectorActive = false;

    // Close impersonate dropdown if clicked outside
    const impersonateWrapper = target.closest('.impersonate-select-wrapper');
    if (!impersonateWrapper) {
      this.impersonateDropdownOpen = false;
    }

    // Close bottom section if clicked outside (but not if clicking in user dropdown)
    if (this.bottomSectionExpanded && !userDropdown) {
      const bottomSection = target.closest('.sidebar-bottom');
      const bottomToggle = target.closest('.bottom-section-toggle');

      if (!bottomSection && !bottomToggle) {
        this.bottomSectionExpanded = false;
      }
    }
  }

  trackDcr(_index: number, dcr: UserDcr): string {
    return dcr?.id;
  }

  getDcrLabel(dcr: UserDcr | null | undefined): string {
    if (!dcr) {
      return '';
    }
    const candidate = [
      dcr.Name,
      dcr.name,
      (dcr as any).label,
      (dcr as any).Label,
      (dcr as any).title,
      (dcr as any).displayName
    ].find(value => {
      if (value === null || value === undefined) {
        return false;
      }
      return String(value).trim().length > 0;
    });
    if (candidate !== undefined) {
      return String(candidate).trim();
    }
    return dcr.id ? String(dcr.id).trim() : '';
  }

  showDcrSecondaryId(dcr: UserDcr): boolean {
    if (!dcr) {
      return false;
    }
    const label = this.getDcrLabel(dcr);
    const normalizedLabel = label.trim().toLowerCase();
    const normalizedId = (dcr.id || '').trim().toLowerCase();
    if (!normalizedLabel || !normalizedId) {
      return false;
    }
    return normalizedLabel !== normalizedId;
  }

  get activeDcrName(): string {
    const currentId = this.authService.getActiveDcrId();
    if (!currentId) {
      return this.translateService.instant('sidebar.dcr.select');
    }
    const match = this.getCurrentDcrOptions().find(dcr => dcr.id === currentId);
    return match ? this.getDcrLabel(match) : currentId;
  }

  get filteredDcrs(): UserDcr[] {
    const available = this.getCurrentDcrOptions();
    const term = this.dcrSearchTerm.trim().toLowerCase();
    if (!term) {
      return available;
    }
    return available.filter(dcr => {
      const name = this.getDcrLabel(dcr).toLowerCase();
      const id = (dcr.id || '').toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  }

  get activeDcrId(): string | null {
    return this.authService.getActiveDcrId();
  }

  get dcrPickerDisabled(): boolean {
    if (this.isInCreationMode) {
      return true;
    }
    return this.getCurrentDcrOptions().length === 0;
  }

  toggleDcrList(event: Event): void {
    event.stopPropagation();
    if (this.getCurrentDcrOptions().length === 0) {
      return;
    }
    this.dcrDropdownOpen = !this.dcrDropdownOpen;
    if (this.dcrDropdownOpen) {
      this.userMenuOpen = false;
      this.entitySelectorActive = true;
      setTimeout(() => {
        const active = this.dcrOptionsList?.nativeElement.querySelector('li.active') as HTMLElement | null;
        active?.scrollIntoView({ block: 'nearest' });
      });
    }
    if (!this.dcrDropdownOpen) {
      this.dcrSearchTerm = '';
      this.keyboardFocusedIndex = -1;
    }
  }

  async selectProduct(product: ProductOption, event?: Event): Promise<void> {
    event?.stopPropagation();

    if (product === this.selectedProduct) {
      return;
    }

    // Block Dashboard selection if not available
    if (product === 'dashboard' && !this.canAccessAnalyticsDashboard) {
      return;
    }

    // Block Vision selection if not available
    if (product === 'vision' && !this.isVisionAvailable) {
      return;
    }

    // Block Reach selection if not available
    if (product === 'reach' && !this.isReachAvailable) {
      return;
    }

    // Block Tracking Scripts selection if not available
    if (product === 'tracking-scripts' && !this.canAccessTrackingScripts) {
      return;
    }

    // Block CMP selection if not available
    if (product === 'cmp' && !this.canAccessCmp) {
      return;
    }

    const targetCommands =
      product === 'home'
        ? ['/home']
        : product === 'dashboard'
          ? ['/dashboard']
          : product === 'vision'
          ? ['/vision/public']
          : product === 'tracking-scripts'
            ? ['/tracking-scripts/configs']
            : product === 'cmp'
              ? ['/cmp/configs']
              : ['/reach/audiences'];

    const allowed = await this.runWithNavigationBlock(async () => {
      await this.router.navigate(targetCommands);
    }, { expectRouteChange: true });
    if (allowed) {
      this.selectedProduct = product;
      this.accordionExpanded = true;
    }
  }

  // Accordion product headers (see accordionMenuEnabled): clicking the header of the
  // already-selected product must collapse/expand its content in place, not no-op like
  // selectProduct does — selectProduct is only for switching to a *different* product.
  onAccordionHeaderClick(product: ProductOption, event?: Event): void {
    if (product === this.selectedProduct) {
      event?.stopPropagation();
      this.accordionExpanded = !this.accordionExpanded;
      return;
    }
    this.selectProduct(product, event);
  }

  onDcrSearch(event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement | null;
    this.dcrSearchTerm = input?.value ?? '';
    this.keyboardFocusedIndex = -1;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const active = document.activeElement;
    if (active && ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)) return;

    const isModified = event.ctrlKey || event.metaKey;
    const canNavigate = isModified || (this.entitySelectorActive && !this.dcrDropdownOpen);
    if (!canNavigate || this.dcrPickerDisabled) return;

    event.preventDefault();
    this.navigateEntityByStep(event.key === 'ArrowDown' ? 1 : -1);
    if (isModified) this.entitySelectorActive = true;
  }

  private navigateEntityByStep(step: 1 | -1): void {
    const selectable = this.getCurrentDcrOptions().filter(dcr => this.canSelectDcr(dcr));
    if (!selectable.length) return;
    const currentId = this.authService.getActiveDcrId();
    const pos = selectable.findIndex(d => d.id === currentId);
    const nextPos = step === 1
      ? (pos === -1 ? 0 : (pos + 1) % selectable.length)
      : (pos === -1 ? selectable.length - 1 : (pos - 1 + selectable.length) % selectable.length);
    this.selectDcr(selectable[nextPos]);
  }

  onDcrKeydown(event: KeyboardEvent): void {
    const selectableIndices = this.filteredDcrs
      .map((dcr, i) => ({ dcr, i }))
      .filter(({ dcr }) => this.canSelectDcr(dcr))
      .map(({ i }) => i);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!selectableIndices.length) return;
      const pos = selectableIndices.indexOf(this.keyboardFocusedIndex);
      this.keyboardFocusedIndex = selectableIndices[pos === -1 ? 0 : (pos + 1) % selectableIndices.length];
      this.scrollFocusedIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!selectableIndices.length) return;
      const pos = selectableIndices.indexOf(this.keyboardFocusedIndex);
      this.keyboardFocusedIndex = selectableIndices[pos === -1 ? selectableIndices.length - 1 : (pos - 1 + selectableIndices.length) % selectableIndices.length];
      this.scrollFocusedIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.keyboardFocusedIndex >= 0) {
        const dcr = this.filteredDcrs[this.keyboardFocusedIndex];
        if (dcr) this.selectDcr(dcr);
      }
    } else if (event.key === 'Escape') {
      this.dcrDropdownOpen = false;
      this.dcrSearchTerm = '';
      this.keyboardFocusedIndex = -1;
    }
  }

  private scrollFocusedIntoView(): void {
    setTimeout(() => {
      const list = this.dcrOptionsList?.nativeElement;
      if (!list) return;
      (list.querySelector('.keyboard-focused') as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
    });
  }

  async selectDcr(dcr: UserDcr, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.canSelectDcr(dcr)) {
      return;
    }
    const allowed = await this.runWithNavigationBlock(async () => {
      this.authService.setActiveDcr(dcr.id);
    });
    if (!allowed) {
      return;
    }
    this.dcrDropdownOpen = false;
    this.userMenuOpen = false;
    this.dcrSearchTerm = '';
    this.keyboardFocusedIndex = -1;
    this.entitySyncPending$.pipe(filter(p => !p), take(1)).subscribe(() => {
      this.entitySelectorActive = true;
      // Selecting an entity without access to the current product (e.g. switching
      // to "Admin" while on Vision) must leave that product, otherwise its views
      // error out with no valid DCR context.
      this.redirectIfCurrentProductUnavailable();
    });
  }

  canDcrAccessVision(dcr: UserDcr | null): boolean {
    return !!dcr?.hasVisionAccess;
  }

  async logout(event?: Event): Promise<void> {
    event?.stopPropagation();
    await this.runWithNavigationBlock(async () => {
      this.userMenuOpen = false;
      this.dcrDropdownOpen = false;
      this.dcrSearchTerm = '';
      await this.router.navigate(['/login']);
      this.authService.logout().subscribe({
        error: (error) => {
          console.error('Logout error:', error);
        }
      });
    }, { expectRouteChange: true });
  }

  requestChangePassword(event?: Event): void {
    event?.stopPropagation();
    this.userMenuOpen = false;
    this.dcrDropdownOpen = false;
    this.changePasswordRequested.emit();
  }

  get canManageExperimentalFeatures(): boolean {
    return userHasPermission(
      this.authService.currentUserValue?.permissions,
      UserPermissions.SPECIAL_FEATURES_EXPERIMENTS
    );
  }

  openExperimentalFeaturesPanel(event?: Event): void {
    event?.stopPropagation();
    this.userMenuOpen = false;
    this.dcrDropdownOpen = false;
    this.experimentalFeaturesPanelOpen = true;
  }

  closeExperimentalFeaturesPanel(): void {
    this.experimentalFeaturesPanelOpen = false;
  }

  // Aperto dal "+" nel centro notifiche (output `newNotification`), che fa già
  // il gate su special_features.can_broadcast.
  openNotifyBroadcastPanel(event?: Event): void {
    event?.stopPropagation();
    this.userMenuOpen = false;
    this.dcrDropdownOpen = false;
    this.notifyBroadcastPanelOpen = true;
  }

  closeNotifyBroadcastPanel(): void {
    this.notifyBroadcastPanelOpen = false;
  }

  private normalizeId(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private async runWithNavigationBlock(
    action: () => void | Promise<void>,
    options?: NavigationBlockOptions
  ): Promise<boolean> {
    return await this.navigationBlockService.runWhenAllowed(action, options);
  }


  getSelectedEntityName(): string {
    const currentId = this.authService.getActiveDcrId();
    if (!currentId) {
      return this.hasGlobalDataAccess
        ? this.translateService.instant('sidebar.dcr.globalScope')
        : this.translateService.instant('sidebar.dcr.select');
    }
    const match = this.getCurrentDcrOptions().find(dcr => dcr.id === currentId);
    return match ? this.getDcrLabel(match) : currentId;
  }

  get isOnDocumentation(): boolean {
    return this.router.url.startsWith('/documentation');
  }

  async openDocumentation(): Promise<void> {
    await this.runWithNavigationBlock(async () => {
      await this.router.navigate(['/documentation']);
    }, { expectRouteChange: true });
  }

  // Navigazione ESTERNA: /console e' un altro deploy (repo tncid-app-console) che il load balancer
  // serve sullo stesso origin, quindi il router di Angular non c'entra e la sessione in
  // localStorage viaggia con la richiesta. Lo slash finale evita il 301 di nginx.
  openConsole(): void {
    window.location.href = '/console/';
  }

  scrollToSection(id: string): void {
    const el = document.getElementById(id);
    console.log(id, el);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 24;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  get filteredCountries(): readonly string[] {
    const term = this.countrySearchTerm.trim().toLowerCase();
    if (!term) {
      return this.availableCountries;
    }
    return this.availableCountries.filter(country =>
      country.toLowerCase().includes(term)
    );
  }

  onCountrySearch(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.countrySearchTerm = input?.value ?? '';
  }

  selectCountry(country: string): void {
    // For now, this is disabled and does nothing
    // this.selectedCountry = country;
  }

  toggleBottomSection(event?: Event): void {
    event?.stopPropagation();
    this.bottomSectionExpanded = !this.bottomSectionExpanded;
    if (this.bottomSectionExpanded && this.canImpersonate && this.impersonateUsers.length === 0) {
      this.loadImpersonateUsers();
    }
  }

  loadImpersonateUsers(): void {
    this.impersonateLoading = true;
    this.impersonateApiService.getUsers().subscribe({
      next: users => {
        this.impersonateUsers = users;
        this.impersonateLoading = false;
      },
      error: () => { this.impersonateLoading = false; }
    });
  }

  onImpersonateChange(userId: string): void {
    if (!userId) {
      if (this.authService.isImpersonating) {
        this.authService.stopImpersonation();
        this.selectedImpersonateUserId = '';
      }
      return;
    }
    this.impersonateApiService.impersonateUser(userId).subscribe({
      next: user => {
        this.authService.startImpersonation(user);
        this.selectedImpersonateUserId = userId;
      },
      error: err => {
        console.error('[sidebar] impersonation failed:', err);
        this.selectedImpersonateUserId = this.authService.isImpersonating
          ? (this.authService.currentUserValue?.ID ?? '')
          : '';
      }
    });
  }

  get selectedImpersonateUserLabel(): string {
    if (!this.selectedImpersonateUserId) {
      return this.translateService.instant('sidebar.impersonate.myself');
    }
    const user = this.impersonateUsers.find(u => u.ID === this.selectedImpersonateUserId);
    return user ? (user.name || user.email) : this.selectedImpersonateUserId;
  }

  toggleImpersonateDropdown(event: Event): void {
    event.stopPropagation();
    if (!this.impersonateDropdownOpen && this.impersonateBtnRef) {
      const rect = this.impersonateBtnRef.nativeElement.getBoundingClientRect();
      this.impersonateDropdownStyle = {
        bottom: (window.innerHeight - rect.top + 4) + 'px',
        left: rect.left + 'px',
        width: rect.width + 'px'
      };
    }
    this.impersonateDropdownOpen = !this.impersonateDropdownOpen;
  }

  selectImpersonateFromDropdown(userId: string, event: Event): void {
    event.stopPropagation();
    this.impersonateDropdownOpen = false;
    this.onImpersonateChange(userId);
  }

  closeBottomSection(): void {
    this.bottomSectionExpanded = false;
    this.userMenuOpen = false;
    // Close notification center when bottom section collapses
    if (this.notificationCenter) {
      this.notificationCenter.closeDropdown();
    }
  }

  onBottomSectionMouseEnter(): void {
    this.isMouseInBottomSection = true;
  }

  onBottomSectionMouseLeave(): void {
    this.isMouseInBottomSection = false;
    // Close only if mouse is not in user menu and notification center is closed
    setTimeout(() => {
      if (!this.isMouseInBottomSection && !this.isMouseInUserMenu && !this.notificationCenter?.isOpen) {
        this.closeBottomSection();
      }
    }, 50);
  }

  onUserMenuMouseEnter(): void {
    this.isMouseInUserMenu = true;
  }

  onUserMenuMouseLeave(): void {
    this.isMouseInUserMenu = false;
    // Close only if mouse is not in bottom section
    setTimeout(() => {
      if (!this.isMouseInBottomSection && !this.isMouseInUserMenu) {
        this.closeBottomSection();
      }
    }, 50);
  }

  changeLanguage(lang: string, event?: Event): void {
    event?.stopPropagation();
    this.currentLanguage = lang;
    this.translateService.use(lang);
    // Save language preference in localStorage
    localStorage.setItem('language', lang);
  }
}
